/**
 * useExperiment Hook
 *
 * Provides experiment variant assignment with multi-layer caching for optimal performance.
 *
 * Caching Strategy (Priority Chain):
 * 0. URL Parameter Override (dev only) - INSTANT (for testing)
 * 1. AsyncStorage (< 5ms, same browser only) - FASTEST
 * 2. Supabase cache (50ms, cross-device sync) - FAST
 * 3. PostHog API (200-500ms, source of truth) - SLOW
 * 4. Control variant (instant, safe fallback) - SAFEST
 *
 * Developer Testing:
 * In development mode, you can force a specific variant using URL parameters:
 * ?force-experiment-{experimentKey}={variantKey}
 *
 * Example: http://localhost:8081/?force-experiment-pricing-test-001=variant_a
 *
 * @example
 * const { variant, loading, error } = useExperiment('pricing-test-001');
 *
 * if (loading) return <ShimmerLoader />;
 * if (error) console.error(error);
 *
 * return variant.key === 'variant_a'
 *   ? <PricingA />
 *   : <PricingB />;
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Sentry from '@sentry/react-native';
import { useLocalSearchParams } from 'expo-router';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { apiService } from '../services/apiService.js';
import logger from '../utils/logger.js';
import supabase from '../utils/supabaseClient.js';

// ============================================================================
// Constants
// ============================================================================

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const POSTHOG_TIMEOUT_MS = 2000; // 2 second timeout for PostHog API calls
const ASYNC_STORAGE_PREFIX = 'experiment:';

// Control variant returned as fallback
const CONTROL_VARIANT = {
  key: 'control',
  name: 'Control',
};

// ============================================================================
// URL Parameter Override (Developer Testing)
// ============================================================================

/**
 * Checks URL for experiment override parameter (developer testing only).
 *
 * Format: ?force-experiment-{experimentKey}={variantKey}
 * Example: ?force-experiment-pricing-test-001=variant_a
 *
 * IMPORTANT:
 * - Only active in development environment (__DEV__ flag)
 * - Returns null if not in development or no override found
 * - When override is active, NO exposure events should be tracked
 * - Logs warning to console for developer awareness
 *
 * @param {string} experimentKey - The experiment key to check for override
 * @param {Object} searchParams - URL search parameters from useLocalSearchParams
 * @returns {Object|null} - Variant object { key, name } or null
 */
const getExperimentOverrideFromURL = (experimentKey, searchParams) => {
  // Only allow overrides in development mode
  if (!__DEV__) {
    return null;
  }

  try {
    // Construct the parameter name: force-experiment-{experimentKey}
    const paramName = `force-experiment-${experimentKey}`;

    // Check if the parameter exists in the URL
    if (!searchParams || !searchParams[paramName]) {
      return null;
    }

    const variantKey = searchParams[paramName];

    // Validate that the variant key is a non-empty string
    if (typeof variantKey !== 'string' || variantKey.trim() === '') {
      logger.warn(`[useExperiment] Invalid variant key in URL override for ${experimentKey}:`, variantKey);
      return null;
    }

    // Return the variant object
    const variant = {
      key: variantKey,
      name: variantKey, // Use key as name for simplicity
    };

    logger.warn(
      `[useExperiment] 🔧 URL OVERRIDE ACTIVE for ${experimentKey}:`,
      variant,
      '\n⚠️  This is a development-only feature. No exposure events will be tracked.'
    );

    return variant;
  } catch (error) {
    logger.error(`[useExperiment] Error parsing URL override for ${experimentKey}:`, error);
    return null;
  }
};

/**
 * Resolves the raw `force-experiment-{key}` override value (dev only).
 *
 * Primary source is expo-router's `useLocalSearchParams`. However, this hook is
 * consumed from the root `_layout`, where `useLocalSearchParams` does NOT surface
 * a CHILD route's query string (e.g. the param on `/home?force-experiment-...`).
 * So we fall back to reading the raw URL query string directly. This app is
 * web-only, so `window.location.search` is always the source of truth and makes
 * the documented `?force-experiment-{key}={variant}` workflow work from any route.
 *
 * @param {string} experimentKey
 * @param {Object} searchParams - from useLocalSearchParams()
 * @returns {string|null} the variant key string, or null when no override is set
 */
const getForceOverrideParam = (experimentKey, searchParams) => {
  const paramName = `force-experiment-${experimentKey}`;

  // Primary: router params for the currently matched screen.
  const fromRouter = searchParams?.[paramName];
  if (fromRouter) return fromRouter;

  // Fallback (web, dev only): read the raw query string. Covers the root-layout
  // case where useLocalSearchParams returns nothing for a child route's params.
  if (__DEV__ && typeof window !== 'undefined' && window.location?.search) {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get(paramName);
      if (fromUrl) return fromUrl;
    } catch (_e) {
      // Malformed query string; ignore and fall through to no-override.
    }
  }

  return null;
};

// ============================================================================
// Bot Detection
// ============================================================================

/**
 * Checks if the current user agent matches known bot patterns.
 * Bots should always receive the control variant to avoid skewing metrics.
 */
const isBot = () => {
  // Skip bot detection in non-browser environments
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const botPatterns = [
    'googlebot',
    'bingbot',
    'slurp', // Yahoo
    'duckduckbot',
    'baiduspider',
    'yandexbot',
    'facebookexternalhit',
    'twitterbot',
    'linkedinbot',
    'whatsapp',
    'telegrambot',
    'slackbot',
    'discordbot',
    'applebot',
    'crawler',
    'spider',
    'bot',
    'headless',
    'phantom',
    'selenium',
    'webdriver',
  ];

  return botPatterns.some((pattern) => userAgent.includes(pattern));
};

// ============================================================================
// AsyncStorage Cache Helpers
// ============================================================================

/**
 * Retrieves cached experiment assignment from AsyncStorage.
 * Returns null if not found, expired, or invalid.
 */
const getFromAsyncStorage = async (experimentKey) => {
  try {
    const cacheKey = `${ASYNC_STORAGE_PREFIX}${experimentKey}`;
    const cachedData = await AsyncStorage.getItem(cacheKey);

    if (!cachedData) {
      logger.log(`[useExperiment] AsyncStorage miss for ${experimentKey}`);
      return null;
    }

    const parsed = JSON.parse(cachedData);
    const now = Date.now();

    // Validate cache structure
    if (!parsed.experimentKey || !parsed.variantKey || !parsed.expiresAt) {
      logger.warn(`[useExperiment] Invalid cache structure for ${experimentKey}`, parsed);
      await AsyncStorage.removeItem(cacheKey); // Clean up invalid cache
      return null;
    }

    // Check if expired
    if (now > parsed.expiresAt) {
      logger.log(`[useExperiment] AsyncStorage cache expired for ${experimentKey}`);
      await AsyncStorage.removeItem(cacheKey); // Clean up expired cache
      return null;
    }

    // Validate experimentKey matches (prevents cache collision)
    if (parsed.experimentKey !== experimentKey) {
      logger.warn(`[useExperiment] Cache experimentKey mismatch for ${experimentKey}`);
      await AsyncStorage.removeItem(cacheKey);
      return null;
    }

    logger.log(`[useExperiment] AsyncStorage hit for ${experimentKey}: ${parsed.variantKey}`);
    return {
      key: parsed.variantKey,
      name: parsed.variantName || parsed.variantKey,
    };
  } catch (error) {
    logger.error(`[useExperiment] AsyncStorage read error for ${experimentKey}:`, error);
    Sentry.captureException(error, {
      tags: {
        context: 'useExperiment:asyncStorageRead',
        experimentKey,
      },
    });
    return null;
  }
};

/**
 * Writes experiment assignment to AsyncStorage with 24-hour TTL.
 */
const saveToAsyncStorage = async (experimentKey, variantKey, variantName) => {
  try {
    const cacheKey = `${ASYNC_STORAGE_PREFIX}${experimentKey}`;
    const now = Date.now();
    const expiresAt = now + CACHE_TTL_MS;

    const cacheData = {
      experimentKey,
      variantKey,
      variantName,
      assignedAt: now,
      expiresAt,
    };

    await AsyncStorage.setItem(cacheKey, JSON.stringify(cacheData));
    logger.log(`[useExperiment] Saved to AsyncStorage: ${experimentKey} = ${variantKey}`);
  } catch (error) {
    logger.error(`[useExperiment] AsyncStorage write error for ${experimentKey}:`, error);
    // Non-blocking: Don't throw, just log
    Sentry.captureException(error, {
      tags: {
        context: 'useExperiment:asyncStorageWrite',
        experimentKey,
      },
    });
  }
};

// ============================================================================
// Supabase Cache Helpers
// ============================================================================

/**
 * Retrieves cached experiment assignment from Supabase using RPC function.
 * Returns null if not found, expired, or invalid.
 */
const getFromSupabaseCache = async (experimentKey, userId, anonymousId) => {
  try {
    // Must have either userId or anonymousId
    if (!userId && !anonymousId) {
      logger.log(`[useExperiment] No user ID or anonymous ID, skipping Supabase cache for ${experimentKey}`);
      return null;
    }

    // Call RPC function (bypasses RLS, secure by design)
    const { data, error } = await supabase.rpc('get_user_experiment_assignment', {
      p_user_id: userId || null,
      p_anonymous_id: anonymousId || null,
      p_experiment_key: experimentKey,
    });

    if (error) {
      logger.error(`[useExperiment] Supabase RPC error for ${experimentKey}:`, error);
      Sentry.captureException(error, {
        tags: {
          context: 'useExperiment:supabaseCacheRPC',
          experimentKey,
          errorCode: error.code,
        },
      });
      return null;
    }

    // RPC returns array, check if we got a result
    if (!data || data.length === 0) {
      logger.log(`[useExperiment] Supabase cache miss for ${experimentKey}`);
      return null;
    }

    const assignment = data[0];

    // Check if cache is still valid
    if (!assignment.cache_valid) {
      logger.log(`[useExperiment] Supabase cache expired for ${experimentKey}`);
      return null;
    }

    logger.log(`[useExperiment] Supabase cache hit for ${experimentKey}: ${assignment.variant_key}`);
    return {
      key: assignment.variant_key,
      name: assignment.variant_name || assignment.variant_key,
    };
  } catch (error) {
    logger.error(`[useExperiment] Supabase cache error for ${experimentKey}:`, error);
    Sentry.captureException(error, {
      tags: {
        context: 'useExperiment:supabaseCache',
        experimentKey,
      },
    });
    return null;
  }
};

// ============================================================================
// PostHog Helpers
// ============================================================================

/**
 * Retrieves variant assignment from PostHog (source of truth).
 * Implements 2-second timeout to prevent UI hanging.
 */
const getFromPostHog = async (posthog, experimentKey) => {
  try {
    if (!posthog) {
      logger.warn(`[useExperiment] PostHog not initialized for ${experimentKey}`);
      return null;
    }

    // Create timeout promise
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('PostHog API timeout')), POSTHOG_TIMEOUT_MS)
    );

    // Race between PostHog API call and timeout
    const featureFlagValue = await Promise.race([
      posthog.getFeatureFlag(experimentKey),
      timeoutPromise,
    ]);

    if (!featureFlagValue) {
      logger.log(`[useExperiment] PostHog returned null for ${experimentKey} (experiment not found or user not in rollout)`);
      return CONTROL_VARIANT;
    }

    // Fetch variant metadata from experiments table to get proper variant name
    const { data: experiment, error: experimentError } = await supabase
      .from('experiments')
      .select('variants')
      .eq('experiment_key', experimentKey)
      .single();

    if (experimentError || !experiment) {
      logger.warn(`[useExperiment] Could not fetch experiment metadata for ${experimentKey}:`, experimentError);
      // Fallback: use key as name
      return {
        key: featureFlagValue,
        name: featureFlagValue,
      };
    }

    // Find variant by key in the variants object (variants is a dictionary, not an array)
    const variant = experiment.variants?.[featureFlagValue];

    if (!variant) {
      logger.warn(`[useExperiment] Variant ${featureFlagValue} not found in experiment metadata`);
      // Fallback: use key as name
      return {
        key: featureFlagValue,
        name: featureFlagValue,
      };
    }

    logger.log(`[useExperiment] PostHog assignment for ${experimentKey}: ${variant.name} (${featureFlagValue})`);
    return {
      key: featureFlagValue,
      name: variant.name, // ✅ Correct: Use proper variant name from experiments table
    };
  } catch (error) {
    if (error.message === 'PostHog API timeout') {
      logger.warn(`[useExperiment] PostHog timeout for ${experimentKey}`);
      Sentry.captureMessage(`PostHog timeout for experiment ${experimentKey}`, {
        level: 'warning',
        tags: {
          context: 'useExperiment:posthogTimeout',
          experimentKey,
        },
      });
    } else {
      logger.error(`[useExperiment] PostHog error for ${experimentKey}:`, error);
      Sentry.captureException(error, {
        tags: {
          context: 'useExperiment:posthog',
          experimentKey,
        },
      });
    }
    return null;
  }
};

/**
 * Tracks exposure event to PostHog (fired IMMEDIATELY upon assignment).
 * This prevents Sample Ratio Mismatch (SRM) issues.
 *
 * Fetches experiment_layer from Supabase experiments table to include in event.
 * This is FIRE-AND-FORGET: exposure event is sent immediately without blocking.
 * The layer is fetched asynchronously and added when available.
 *
 * Uses sessionStorage for deduplication (one event per session).
 */
const trackExposureEvent = (posthog, experimentKey, variantKey, variantName) => {
  // Fire-and-forget: don't await, don't block variant rendering
  (async () => {
    try {
      if (!posthog) {
        logger.warn(`[useExperiment] Cannot track exposure: PostHog not initialized`);
        return;
      }

      // Check if already tracked this session (deduplication)
      const sessionKey = `experiment_exposure_${experimentKey}`;
      if (typeof sessionStorage !== 'undefined') {
        if (sessionStorage.getItem(sessionKey)) {
          logger.log(`[useExperiment] ⏭️  Exposure already tracked for ${experimentKey} this session`);
          return;
        }
        // Mark as tracked
        sessionStorage.setItem(sessionKey, 'true');
      }

      // Fetch experiment_layer from Supabase (non-blocking)
      let experimentLayer = null;
      try {
        const { data, error } = await supabase
          .from('experiments')
          .select('experiment_layer')
          .eq('experiment_key', experimentKey)
          .eq('status', 'active')
          .single();

        if (!error && data) {
          experimentLayer = data.experiment_layer;
          logger.log(`[useExperiment] Fetched experiment_layer for ${experimentKey}: ${experimentLayer}`);
        } else if (error) {
          logger.warn(`[useExperiment] Could not fetch experiment_layer for ${experimentKey}:`, error);
        }
      } catch (layerError) {
        // Non-blocking: Log error but continue with exposure tracking
        logger.warn(`[useExperiment] Error fetching experiment_layer for ${experimentKey}:`, layerError);
      }

      // Build event properties
      const eventProperties = {
        experiment_key: experimentKey,
        variant_key: variantKey,
        variant_name: variantName,
        timestamp: Date.now(),
      };

      // Add experiment_layer if available
      if (experimentLayer) {
        eventProperties.experiment_layer = experimentLayer;
      }

      posthog.capture('experiment_viewed', eventProperties);

      logger.log(
        `[useExperiment] Tracked exposure for ${experimentKey}: ${variantKey}`,
        experimentLayer ? `(layer: ${experimentLayer})` : '(no layer)'
      );
    } catch (error) {
      logger.error(`[useExperiment] Error tracking exposure for ${experimentKey}:`, error);
      // Non-blocking: Don't throw, analytics failure shouldn't break UX
      Sentry.captureException(error, {
        tags: {
          context: 'useExperiment:trackExposure',
          experimentKey,
        },
      });
    }
  })();
};

// ============================================================================
// Edge Function Helpers
// ============================================================================

/**
 * Calls the cache-experiment-assignment edge function to persist assignment to Supabase.
 * This is non-blocking and fire-and-forget.
 */
const cacheAssignmentToSupabase = async (
  experimentKey,
  variantKey,
  variantName,
  userId,
  anonymousId,
  posthog,
  exposureTracked = false
) => {
  try {
    const posthogDistinctId = posthog?.getDistinctId?.() || null;

    const result = await apiService.invokeEdgeFunction('cache-experiment-assignment', {
      body: {
        userId: userId || null,
        anonymousId: anonymousId || null,
        experimentKey,
        variantKey,
        variantName,
        posthogDistinctId,
        exposureTracked,
      },
    });

    if (result?.error) {
      logger.warn(`[useExperiment] Edge function error for ${experimentKey}:`, result.error);
      Sentry.captureMessage(`Edge function cache error: ${result.error}`, {
        level: 'warning',
        tags: {
          context: 'useExperiment:cacheEdgeFunction',
          experimentKey,
        },
        extra: {
          errorMessage: result.error,
        },
      });
    } else {
      logger.log(`[useExperiment] Cached to Supabase via edge function: ${experimentKey} = ${variantKey}`);
    }
  } catch (error) {
    logger.warn(`[useExperiment] Edge function call failed for ${experimentKey}:`, error);
    // Non-blocking: Don't throw, cache failure shouldn't break UX
    Sentry.captureMessage(`Edge function call failed: ${error.message}`, {
      level: 'warning',
      tags: {
        context: 'useExperiment:cacheEdgeFunction',
        experimentKey,
      },
      extra: {
        errorName: error.name,
        errorMessage: error.message,
      },
    });
  }
};

// ============================================================================
// Cookie Consent Helper
// ============================================================================

/**
 * Checks if user has provided analytics consent via PostHog.
 * Returns true if consent granted or unknown (opt-in by default).
 * Returns false if explicitly declined.
 */
const hasAnalyticsConsent = (posthog) => {
  try {
    if (!posthog) {
      // If PostHog not initialized, assume consent pending
      logger.log(`[useExperiment] PostHog not initialized, assuming consent pending`);
      return true; // Assume consent for graceful degradation
    }

    // Check PostHog's has_opted_out_capturing() method
    // If opted out, return false
    if (posthog.has_opted_out_capturing && posthog.has_opted_out_capturing()) {
      logger.log(`[useExperiment] User has opted out of tracking`);
      return false;
    }

    // Default: consent granted or pending (allow experiments)
    return true;
  } catch (error) {
    logger.error(`[useExperiment] Error checking consent:`, error);
    // Default to true for graceful degradation
    return true;
  }
};

// ============================================================================
// Main Hook
// ============================================================================

/**
 * useExperiment Hook
 *
 * @param {string} experimentKey - The kebab-case experiment key (e.g., 'pricing-test-001')
 * @returns {Object} { variant, loading, error }
 *   - variant: { key: string, name: string } - The assigned variant
 *   - loading: boolean - True while variant is being resolved
 *   - error: Error | null - Error object if something went wrong
 */
export const useExperiment = (experimentKey) => {
  const { user } = useAuth();
  const posthog = usePostHog();
  const searchParams = useLocalSearchParams();

  const [variant, setVariant] = useState(CONTROL_VARIANT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track whether URL override is active (to suppress exposure events)
  const urlOverrideActiveRef = useRef(false);

  // Get user identifiers
  const userId = user?.id || null;
  const anonymousId = posthog?.getDistinctId?.() || null;

  // Extract the specific URL parameter we need (to avoid searchParams object causing infinite loops).
  // Falls back to the raw window.location query string so the dev override works
  // from any route, not just the one whose params useLocalSearchParams surfaces.
  const urlOverrideParam = getForceOverrideParam(experimentKey, searchParams);

  /**
   * Resolves variant assignment using the priority chain:
   * URL Override (dev only) → AsyncStorage → Supabase → PostHog → Control
   */
  const resolveVariant = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // ====================================================================
      // Developer Testing: URL Parameter Override (HIGHEST PRIORITY)
      // ====================================================================
      // This check MUST happen FIRST, before any cache or API calls.
      // When URL override is active:
      // 1. Return the overridden variant immediately
      // 2. Skip all caching layers and PostHog API
      // 3. Do NOT track exposure events (data pollution prevention)
      // 4. Only works in development mode (__DEV__ flag)

      // Build a minimal searchParams object with only the parameter we need
      const overrideSearchParams = urlOverrideParam ? { [`force-experiment-${experimentKey}`]: urlOverrideParam } : {};
      const urlOverrideVariant = getExperimentOverrideFromURL(experimentKey, overrideSearchParams);

      if (urlOverrideVariant) {
        // Mark override as active to suppress exposure tracking
        urlOverrideActiveRef.current = true;

        setVariant(urlOverrideVariant);
        setLoading(false);

        logger.log(
          `[useExperiment] ✅ Returning URL override variant for ${experimentKey}:`,
          urlOverrideVariant
        );

        return; // Exit early - no caching, no API calls, no exposure events
      }

      // Reset override flag if no override is active
      urlOverrideActiveRef.current = false;

      // ====================================================================
      // Pre-flight Checks
      // ====================================================================

      // Check 1: Validate experiment key format
      if (!experimentKey || typeof experimentKey !== 'string') {
        logger.warn(`[useExperiment] Invalid experiment key:`, experimentKey);
        setVariant(CONTROL_VARIANT);
        setLoading(false);
        return;
      }

      // Check 2: Bot detection
      if (isBot()) {
        logger.log(`[useExperiment] Bot detected, returning control for ${experimentKey}`);
        setVariant(CONTROL_VARIANT);
        setLoading(false);
        return;
      }

      // Check 3: Cookie consent
      if (!hasAnalyticsConsent(posthog)) {
        logger.log(`[useExperiment] No analytics consent, returning control for ${experimentKey}`);
        setVariant(CONTROL_VARIANT);
        setLoading(false);
        return;
      }

      // ====================================================================
      // Caching Layer 1: AsyncStorage (< 5ms)
      // ====================================================================

      const cachedVariant = await getFromAsyncStorage(experimentKey);
      if (cachedVariant) {
        setVariant(cachedVariant);
        setLoading(false);

        // Track exposure event (deduplication handled in trackExposureEvent)
        trackExposureEvent(posthog, experimentKey, cachedVariant.key, cachedVariant.name);

        return;
      }

      // ====================================================================
      // Caching Layer 2: Supabase (50ms)
      // ====================================================================

      const supabaseCachedVariant = await getFromSupabaseCache(experimentKey, userId, anonymousId);
      if (supabaseCachedVariant) {
        setVariant(supabaseCachedVariant);
        setLoading(false);

        // Cache to AsyncStorage for faster future lookups
        await saveToAsyncStorage(
          experimentKey,
          supabaseCachedVariant.key,
          supabaseCachedVariant.name
        );

        // Track exposure event (deduplication handled in trackExposureEvent)
        trackExposureEvent(
          posthog,
          experimentKey,
          supabaseCachedVariant.key,
          supabaseCachedVariant.name
        );

        return;
      }

      // ====================================================================
      // Source of Truth: PostHog API (200-500ms)
      // ====================================================================

      const posthogVariant = await getFromPostHog(posthog, experimentKey);

      if (posthogVariant) {
        setVariant(posthogVariant);
        setLoading(false);

        // Cache to AsyncStorage
        await saveToAsyncStorage(experimentKey, posthogVariant.key, posthogVariant.name);

        // Track exposure event (deduplication handled in trackExposureEvent)
        trackExposureEvent(posthog, experimentKey, posthogVariant.key, posthogVariant.name);

        // Cache to Supabase via edge function (fire-and-forget) with exposure tracked
        // Only cache if we have at least one identifier (userId or anonymousId)
        if (userId || anonymousId) {
          cacheAssignmentToSupabase(
            experimentKey,
            posthogVariant.key,
            posthogVariant.name,
            userId,
            anonymousId,
            posthog,
            true // exposureTracked = true (we just sent the exposure event above)
          );
        } else {
          logger.warn(`[useExperiment] Skipping Supabase cache for ${experimentKey}: no userId or anonymousId available`);
        }

        return;
      }

      // ====================================================================
      // Fallback: Control Variant
      // ====================================================================

      logger.log(`[useExperiment] All sources failed/unavailable, returning control for ${experimentKey}`);
      setVariant(CONTROL_VARIANT);
      setLoading(false);

    } catch (err) {
      logger.error(`[useExperiment] Unexpected error resolving variant for ${experimentKey}:`, err);
      setError(err);
      setVariant(CONTROL_VARIANT);
      setLoading(false);

      Sentry.captureException(err, {
        tags: {
          context: 'useExperiment:resolveVariant',
          experimentKey,
        },
      });
    }
  }, [experimentKey, userId, anonymousId, posthog, urlOverrideParam]);

  // ====================================================================
  // Effect: Resolve variant on mount or when dependencies change
  // ====================================================================

  useEffect(() => {
    resolveVariant();
  }, [resolveVariant]);

  return {
    variant,
    loading,
    error,
  };
};

export default useExperiment;
